import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container } from '@/components/layout/Container'
import { AccountSidebar } from '@/components/account/AccountSidebar'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import { SignedInOnly } from '@/components/account/SignedInOnly'
import { updateProfile, changePassword, deleteAccount } from '@/lib/api'

export function AccountDetailsPage() {
  const navigate = useNavigate()
  const { user, refreshUser, signOut } = useAuth()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSaved, setProfileSaved] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSaved, setPasswordSaved] = useState(false)

  const [deletingAccount, setDeletingAccount] = useState(false)
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null)

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  useEffect(() => {
    if (user) {
      setName(user.name ?? '')
      setPhone(user.phone ?? '')
    }
  }, [user?.id, user?.name, user?.phone])

  const handleProfileSave = async () => {
    setSavingProfile(true)
    setProfileError(null)
    setProfileSaved(false)
    try {
      const updated = await updateProfile({ name, phone: phone || null })
      if (updated) {
        await refreshUser()
        setProfileSaved(true)
      } else {
        setProfileError('Could not update your details.')
      }
    } finally {
      setSavingProfile(false)
    }
  }

  const handlePasswordChange = async () => {
    setPasswordError(null)
    setPasswordSaved(false)
    if (newPassword.length < 8) {
      setPasswordError('Your new password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.')
      return
    }
    setSavingPassword(true)
    const result = await changePassword(currentPassword, newPassword)
    setSavingPassword(false)
    if (result.success) {
      setPasswordSaved(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } else {
      const messages: Record<string, string> = {
        google_account_no_password: 'This account uses Google sign-in and has no password.',
        current_password_incorrect: 'Your current password is incorrect.',
        invalid_request: 'Please fill in all fields.',
      }
      setPasswordError(messages[result.error] ?? 'Could not change your password.')
    }
  }

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account?\n\nThis cannot be undone. Your order history will be kept for our records, but it will no longer be linked to your profile.'
    )
    if (!confirmed) return

    setDeletingAccount(true)
    setDeleteAccountError(null)
    try {
      const result = await deleteAccount()
      if (result.success) {
        await signOut()
        navigate('/')
      } else {
        setDeleteAccountError('Could not delete your account. Please try again or contact support.')
      }
    } finally {
      setDeletingAccount(false)
    }
  }

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-6xl">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <AccountSidebar activePath="/account/details" />

          <SignedInOnly>
          <div className="max-w-xl">
            <p className="mb-2 text-xs uppercase tracking-[0.2em] text-wd-orange">account</p>
            <h1 className="font-display text-4xl normal-case text-carbon md:text-5xl">Details and Security</h1>

            <section className="mt-10">
              <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-carbon">
                Your details
              </h2>
              {profileError && <p className="mb-4 text-sm text-carbon">{profileError}</p>}
              {profileSaved && <p className="mb-4 text-sm text-carbon/60">Details updated.</p>}
              <div className="space-y-4">
                <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                <Input
                  label="Email"
                  value={user?.email ?? ''}
                  readOnly
                  className="text-carbon/40"
                />
                <Input
                  label="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+234..."
                />
              </div>
              <Button onClick={handleProfileSave} isLoading={savingProfile} className="mt-6">
                Save details
              </Button>
            </section>

            {user?.authProvider === 'local' && (
              <section className="mt-14">
                <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-carbon">
                  Change password
                </h2>
                {passwordError && <p className="mb-4 text-sm text-carbon">{passwordError}</p>}
                {passwordSaved && <p className="mb-4 text-sm text-carbon/60">Password updated.</p>}
                <div className="space-y-4">
                  <Input
                    label="Current password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <Input
                    label="New password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <Input
                    label="Confirm new password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <Button onClick={handlePasswordChange} isLoading={savingPassword} className="mt-6">
                  Update password
                </Button>
              </section>
            )}

            <section className="mt-14 border-t border-ink/10 pt-10">
              <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-carbon">
                Delete account
              </h2>
              <p className="mb-4 text-sm text-carbon/60">
                Deleting your account will remove your profile, addresses, wishlist and preferences.
                Order history is retained for record-keeping but will no longer be linked to your identity.
                This cannot be undone.
              </p>
              {deleteAccountError && (
                <p className="mb-4 text-sm text-carbon">{deleteAccountError}</p>
              )}
              <Button
                variant="danger"
                onClick={handleDeleteAccount}
                isLoading={deletingAccount}
              >
                Delete account
              </Button>
            </section>
          </div>
          </SignedInOnly>
        </div>
      </Container>
    </div>
  )
}